// Loosely based on: https://github.com/auth0/auth0-react/
import { createContext } from "react";
import type { AuthenticateError, AuthenticateSuccess, AuthUserResult, NateraIntakePacketInput, ResendMagicLinkResult, SendMagicLinkResult, ShadowLoginInput } from "@medbillai/generated-graphql-types";
import type { OAuthProviderType } from "@medbillai/ui";
import { getStartedUrl, loginUrl } from "@medbillai/utils";
import type { StytchTokenType } from "../../provider/stytch";
import type { UserSegmentationData } from "../sign-up-onboarding/signUpOnboardingStore";
import { type AuthState, initialAuthState, type User } from "./authState";
export const authPaths = [loginUrl(),
// These paths require params to be passed in so we use the string instead of
// the function from utils.
"/verify-email", "/authenticate", getStartedUrl()];

/**
 * Partner bound at account creation via an FFGA invite — surfaced on the
 * authenticate result so the caller can route into the partner onboarding.
 */
export type AuthenticatedPartner = {
  slug: string;
  name: string;
};

/** `hasPendingNateraIntake` is absent when the mutation does not answer it. */
export type AuthenticateOutcome = AuthenticateError | (Omit<AuthenticateSuccess, "hasPendingNateraIntake" | "partner"> & {
  hasPendingNateraIntake?: boolean;
  partner?: AuthenticatedPartner;
});
export interface AuthContextInterface<TUser extends User = User> extends AuthState<TUser> {
  /**
   * The user snapshot carries profile-derived fields (partner, terms,
   * profile) — rehydrated from storage or freshly fetched. False only in
   * the window between a fresh login and its first profile load.
   */
  isProfileLoaded: boolean;
  startOAuth: (provider: OAuthProviderType, inviteToken?: string) => Promise<void>;
  sendMagicLink: (params: {
    email: string;
    botControlToken?: string;
    userSegmentationData?: UserSegmentationData;
    inviteToken?: string;
    /** Pre-auth funnel answers, stored server-side against the user this send creates. */
    nateraIntake?: NateraIntakePacketInput;
  }) => Promise<SendMagicLinkResult & {
    /** What was actually sent, so the caller can report it. */
    deliveryMethod?: "MAGIC_LINK" | "EMAIL_OTP" | "CROSS_BROWSER_LINK";
  }>;
  resendMagicLink: (params: {
    email: string;
    /** Forces the method regardless of the email-otp-login flag. */
    useEmailOtp?: boolean;
    /** Forces a cross-browser link regardless of the browser and its flag. */
    crossBrowserLink?: boolean;
  }) => Promise<ResendMagicLinkResult>;
  authenticate: (params: {
    method: StytchTokenType;
    token: string;
    codeVerifier?: string;
    /** Required for email_otp: the address the code was sent to. */
    email?: string;
    userSegmentationData?: UserSegmentationData;
    inviteToken?: string;
    nateraIntake?: NateraIntakePacketInput;
  }) => Promise<AuthenticateOutcome>;
  logout: () => Promise<void>;
  shadowLogin: (input: ShadowLoginInput) => Promise<AuthUserResult | undefined>;
  setIntendedPath: (path: string) => void;
  getAndClearIntendedPath: () => string | null;
  // Re-fetch the profile into auth state after a post-auth segmentation write
  // (e.g. FFGA onboarding intent) so cached reads reflect it.
  refreshProfile: () => Promise<void>;
}
export const initialContext: AuthContextInterface = {
  ...initialAuthState,
  isProfileLoaded: false,
  // Functions will be set by AuthProvider upon instantiation
  // startOAuth starts the OAuth flow for the given provider.
  startOAuth: () => {
    throw new Error("Context not initialized");
  },
  // sendMagicLink sends a magic login link to the given email address.
  sendMagicLink: () => {
    throw new Error("Context not initialized");
  },
  resendMagicLink: () => {
    throw new Error("Context not initialized");
  },
  // authenticate authenticates the user with either magic link or OAuth token.
  authenticate: () => {
    throw new Error("Context not initialized");
  },
  // logout logs the user out through the API. The user's session cookie is cleared as a result.
  logout: () => {
    throw new Error("Context not initialized");
  },
  // shadowLogin allows an admin user to convert their session to impersonate another user.
  shadowLogin: () => {
    throw new Error("Context not initialized");
  },
  setIntendedPath: () => {
    throw new Error("Context not initialized");
  },
  getAndClearIntendedPath: () => {
    throw new Error("Context not initialized");
  },
  refreshProfile: () => {
    throw new Error("Context not initialized");
  }
};
export const AuthContext = createContext<AuthContextInterface>(initialContext);